/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';

import { type Group, type GroupMap, type GroupMapList, type PermissionListAndMap, type PermitMap, type PermitMapList, type SimpleMap } from './permit';
import { PermissionSuffixE } from './permit.enum';
import { defaultPermitType } from './role.constants';

@Injectable({ providedIn: 'root' })
export class PermitService {
    // 权限列表数据
    permitList: Group[] = [];
    // map：权限的选中，权限组的展开状态
    permitListMap: GroupMapList = {};
    // 选中的权限组数量
    checkedGroupLength = 0;
    // 原始的权限数据，用于修改时比较
    originSimpleMap: SimpleMap = {};
    /**
     * 权限映射表（数组里包含了_R和_W）
     */
    permitHashMap: SimpleMap = {};

    /**
     * 初始化权限映射表
     * @param permitString 接口查到的权限字符串
     */
    updatePermitHashMap(permitString: string): void {
        if (!permitString) {
            return;
        }

        this.permitHashMap = this.stringToSimpleMap(permitString);
    }

    /**
     * 转换权限列表数据
     *
     * @param {SimpleMap} permitList: 权限列表
     * @param {*} [selectedString]: 已选择的权限
     * @returns
     * @memberof PermitService
     */
    doPermitList(permitList: SimpleMap, selectedString?: string): PermissionListAndMap {
        const newList: Group[] = [];
        const permitListMap: GroupMapList = {};
        let checkedGroupLength = 0;

        const selectedMap = this.stringToSimpleMap(selectedString);

        Object.keys(permitList).forEach((key) => {
            const map: PermitMapList = {};
            const group: string[] = permitList[key];
            if (Array.isArray(group)) {
                let checkedLength = 0;
                let isChecked = false;
                const isExpand = false;
                group.forEach((permit) => {
                    // eslint-disable-next-line @typescript-eslint/no-shadow
                    let permitIsChecked = false;
                    let value = defaultPermitType;
                    if (selectedMap[key]) {
                        if (selectedMap[key].indexOf(`${permit}${PermissionSuffixE.READ}`) !== -1) {
                            permitIsChecked = true;
                            value = '1';

                            // 默认都不展开
                            checkedLength++;
                            if (selectedMap[key].indexOf(`${permit}${PermissionSuffixE.WRITE}`) !== -1) {
                                value = '2';
                            }
                        } else {
                            if (selectedMap[key].indexOf(`${permit}${PermissionSuffixE.WRITE}`) !== -1) {
                                // DM设置只写权限,修改时显示只读,详情显示只写,用ischeck区分判断
                                value = '1';
                            }
                        }
                    }
                    map[permit] = {
                        value,
                        isChecked: permitIsChecked,
                    };
                });
                const groupObj: Group = {
                    key,
                    value: group,
                };

                if (checkedLength !== 0 && checkedLength === group.length) {
                    isChecked = true;
                    checkedGroupLength++;
                }

                const groupMap: GroupMap = {
                    permitMap: map,
                    isExpand,
                    isChecked,
                    length: group.length,
                    checkedLength,
                };
                permitListMap[key] = groupMap;
                newList.push(groupObj);
            }
        });
        this.originSimpleMap = selectedMap;
        this.checkedGroupLength = checkedGroupLength;
        this.permitList = newList;
        this.permitListMap = permitListMap;
        return this.getCommonResult();
    }

    /**
     * 处理返回到页面的数据
     *
     * @param {object} [options]
     * @returns
     * @memberof PermitService
     */
    getCommonResult(options?: Record<string, unknown>): PermissionListAndMap {
        return {
            permitList: this.permitList,
            permitListMap: this.permitListMap,
            checkedGroupLength: this.checkedGroupLength,
            originSimpleMap: this.originSimpleMap,
            options,
        };
    }

    // 查找权限组
    getGroupMap(group: string): GroupMap {
        return this.permitListMap[group];
    }

    // 查找权限
    getPermitMap(group: string, permit: string): PermitMap {
        const groupMap = this.getGroupMap(group);
        const permitMap = groupMap.permitMap[permit];
        return permitMap;
    }

    /**
     * 把数据转换成接口字符串
     *
     * @returns
     * @memberof PermitService
     */
    getSelectedPermitString(): string {
        let allPermitString = '';
        for (const permitArr of this.permitList) {
            const permitList = [];
            for (const key of permitArr.value) {
                const permitMap = this.getPermitMap(permitArr.key, key);
                if (permitMap.isChecked) {
                    if (permitMap.value === '1') {
                        permitList.push(`${key}_R`);
                    } else if (permitMap.value === '2') {
                        permitList.push(`${key}_R`);
                        permitList.push(`${key}_W`);
                    }
                }
            }
            if (permitList.length > 0) {
                allPermitString += permitArr.key + ':' + permitList.join(',') + ';';
            }
        }
        return allPermitString;
    }

    /**
     * 修改权限数据
     *
     * @param {string} group
     * @param {*} params
     * @returns
     * @memberof PermitService
     */
    setGroupMap(group: string, params): PermissionListAndMap {
        const permitListMap = this.permitListMap;
        const groupMap = this.getGroupMap(group);

        if (params.permit) {
            const permitMap = this.getPermitMap(group, params.permit);
            if (params.permitCheck !== undefined) {
                const isChecked = permitMap.isChecked;
                permitMap.isChecked = !isChecked;
                if (params.checkedLength === undefined) {
                    let checkedLength = groupMap.checkedLength;
                    if (!isChecked) {
                        checkedLength++;
                    } else {
                        checkedLength--;
                    }
                    params.checkedLength = checkedLength;
                }
            }
            if (params.value !== undefined) {
                permitMap.value = params.value;
            }
            groupMap.permitMap[params.permit] = permitMap;
        }

        if (params.permitMap && params.checkedLength !== undefined) {
            groupMap.permitMap = params.permitMap;
        }

        if (params.checkedLength !== undefined) {
            const length = groupMap.length;
            groupMap.checkedLength = params.checkedLength;
            if (groupMap.checkedLength === length && !groupMap.isChecked) {
                groupMap.isChecked = true;
                this.checkedGroupLength++;
            } else if (groupMap.checkedLength !== length && groupMap.isChecked) {
                groupMap.isChecked = false;
                this.checkedGroupLength--;
            }
        }

        if (params.isExpand !== undefined) {
            groupMap.isExpand = params.isExpand;
        }

        permitListMap[group] = groupMap;
        this.permitListMap = { ...permitListMap };
        return this.getCommonResult();
    }

    // toggle 全选状态
    setAllCheckedStatus(isChecked: boolean): PermissionListAndMap {
        Object.keys(this.permitListMap).forEach((group) => {
            const groupMap = this.permitListMap[group];
            Object.keys(groupMap.permitMap).forEach((permit) => {
                const map = groupMap.permitMap[permit];
                map.isChecked = isChecked;
            });
            groupMap.isChecked = isChecked;
            groupMap.checkedLength = isChecked ? groupMap.length : 0;
        });
        this.checkedGroupLength = isChecked ? this.permitList.length : 0;

        return this.getCommonResult();
    }

    stringToSimpleMap(oriPermitString: string, clearFix?: boolean): SimpleMap {
        const simpleMap: SimpleMap = {};
        let permitString = oriPermitString;
        if (permitString) {
            if (permitString.charAt(permitString.length - 1) === ';') {
                permitString = permitString.slice(0, -1);
            }
            const arr = permitString.split(';');
            arr.forEach((grpAndPermits) => {
                const selectedGroup = grpAndPermits.split(':');
                const selectedKey = selectedGroup[0];
                let selectedPermit = [];
                if (selectedGroup[1]) {
                    if (clearFix) {
                        // eslint-disable-next-line @typescript-eslint/no-shadow
                        selectedGroup[1].split(',').forEach((oriPermitKey) => {
                            let item = oriPermitKey;
                            item = item.replace(/_[RW]$/, '');
                            if (selectedPermit.indexOf(item) === -1) {
                                selectedPermit.push(item);
                            }
                        });
                    } else {
                        selectedPermit = selectedGroup[1].split(',');
                    }
                }
                simpleMap[selectedKey] = selectedPermit;
            });
        }
        return simpleMap;
    }

    simpleMapToString(simpleMap: SimpleMap): string {
        let allPermitString = '';
        Object.keys(simpleMap).forEach((group) => {
            const permitArr = simpleMap[group];
            if (Array.isArray(permitArr) && permitArr.length > 0) {
                allPermitString += `${group}:${permitArr.join(',')};`;
            }
        });
        return allPermitString;
    }

    /**
     * 比较修改前后的权限数据
     *
     * @param {string} newString
     * @param {string} [oldString]
     * @returns
     * @memberof PermitService
     */
    comparePermitString(newString: string, oldString?: string): {
        removeString: string;
        addString: string;
    } {
        const newMap = this.stringToSimpleMap(newString);
        let oldMap = this.originSimpleMap;
        if (oldString) {
            oldMap = this.stringToSimpleMap(oldString);
        }
        const removeMap: SimpleMap = {};
        const addMap: SimpleMap = {};
        Object.keys(newMap).forEach((group) => {
            const oldArr = oldMap[group];
            const newArr = newMap[group];
            if (!newArr || newArr.length === 0) {
                return;
            }
            for (const permit of newArr) {
                if (!oldArr || oldArr.indexOf(permit) === -1) {
                    if (!addMap[group]) {
                        addMap[group] = [];
                    }
                    addMap[group].push(permit);
                }
            }
        });
        Object.keys(oldMap).forEach((group) => {
            const oldArr = oldMap[group];
            const newArr = newMap[group];
            if (!oldArr || oldArr.length === 0) {
                return;
            }
            for (const permit of oldArr) {
                if (!newArr || newArr.indexOf(permit) === -1) {
                    if (!removeMap[group]) {
                        removeMap[group] = [];
                    }
                    removeMap[group].push(permit);
                }
            }
        });
        return {
            removeString: this.simpleMapToString(removeMap),
            addString: this.simpleMapToString(addMap),
        };
    }

    /**
     * 处理当前只读写权限
     * @param groupKey 所属组
     * @param permitKey 权限名
     */
    isReadOnlyPermit(groupKey: string, permitKey: string): boolean {
        const permitArr = this.permitHashMap[groupKey] ?? [];
        if (permitArr.find((permit) => permit === `${permitKey}${PermissionSuffixE.WRITE}`)) {
            return false;
        }

        return true;
    }
}
